import { apiClient } from './api';

export type Department =
  | 'DISTRIBUTION'
  | 'LOGISTICS'
  | 'COOKING'
  | 'STOREFRONT';

export type Weekday =
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY';

// API types matching backend DTOs
export interface CreateVolunteerDto {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  birthDate: string;
  availability?: string;
  address?: string;
  zipCode?: string;
  city?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  emergencyContactRelation?: string;
  motivation?: string;
  experience?: string;
  department: Department[];
  workingHoursPerMonth: number;
  weekdaysAvailable: Weekday[];
  languages?: string;
  dataProtectionConsent: boolean;
}

export interface UserResponse {
  id: string;
  email: string;
  auth0Id: string;
  role: 'SUPER_ADMIN' | 'ADMIN' | 'VOLUNTEER';
  volunteerId: string;
  approved: boolean;
  createdAt: string;
}

export interface VolunteerResponse {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  birthDate: string;
  availability?: string;
  address?: string;
  zipCode?: string;
  city?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  emergencyContactRelation?: string;
  motivation?: string;
  experience?: string;
  department: Department[];
  workingHoursPerMonth: number;
  weekdaysAvailable: Weekday[];
  languages?: string;
  dataProtectionConsent: boolean;
  contractSigned: boolean;
  contractSignedAt?: string;
  healthCertificateUploaded: boolean;
  healthCertificateUploadedAt?: string;
  healthCertificateOriginalName?: string;
  healthCertificateReviewStatus?: 'PENDING' | 'APPROVED' | 'REJECTED';
  healthCertificateReviewedAt?: string;
  attendanceTotal: number;
  user?: UserResponse;
  createdAt: string;
  updatedAt: string;
}

export interface CreateUserDto {
  application: CreateVolunteerDto;
  signatureData: string;
  password: string;
}

export interface UpdateProfileDto {
  firstName?: string;
  lastName?: string;
  phone?: string;
  address?: string;
  zipCode?: string;
  city?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  emergencyContactRelation?: string;
  motivation?: string;
  experience?: string;
  availability?: string;
  department?: Department[];
  workingHoursPerMonth?: number;
  weekdaysAvailable?: Weekday[];
  languages?: string;
}

/**
 * Public API functions - used in the volunteer registration flow.
 * These don't expose volunteer data to the frontend.
 */
export const volunteerApi = {
  previewContract: async (data: CreateVolunteerDto): Promise<Blob> => {
    const response = await apiClient.post<Blob>(
      '/antrag/contract/preview',
      data,
      { responseType: 'blob' }
    );
    return response.data;
  },

  /**
   * Sign the volunteer contract.
   * The volunteer ID is passed but no volunteer data is returned.
   */
  signContract: async (
    id: string,
    signatureDataBase64: string
  ): Promise<Blob> => {
    const response = await apiClient.post(
      `/antrag/contract/sign/${id}`,
      { signatureData: signatureDataBase64 },
      { responseType: 'blob' }
    );
    return response.data;
  },
};

/**
 * Protected API functions - require authentication.
 * Used only in the /app protected routes.
 */
export const protectedVolunteerApi = {
  getAll: async (): Promise<VolunteerResponse[]> => {
    const response = await apiClient.get<VolunteerResponse[]>('/antrag');
    return response.data;
  },

  getById: async (id: string): Promise<VolunteerResponse> => {
    const response = await apiClient.get<VolunteerResponse>(`/antrag/${id}`);
    return response.data;
  },

  getSignedContractUrl: (id: string): string => {
    return `${apiClient.defaults.baseURL}/antrag/${id}/signed-contract`;
  },

  getHealthCertificateUrl: (id: string): string => {
    return `${apiClient.defaults.baseURL}/antrag/${id}/health-certificate`;
  },

  getHealthCertificate: async (id: string): Promise<Blob> => {
    const response = await apiClient.get<Blob>(
      `/antrag/${id}/health-certificate`,
      { responseType: 'blob' }
    );
    return response.data;
  },

  update: async (
    id: string,
    data: Partial<CreateVolunteerDto>
  ): Promise<VolunteerResponse> => {
    const response = await apiClient.put<VolunteerResponse>(
      `/antrag/${id}`,
      data
    );
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/antrag/${id}`);
  },
};

export const userApi = {
  create: async (data: CreateUserDto): Promise<UserResponse> => {
    const response = await apiClient.post<UserResponse>('/user', data);
    return response.data;
  },

  getByVolunteerId: async (
    volunteerId: string
  ): Promise<UserResponse | null> => {
    const response = await apiClient.get<UserResponse | null>(
      `/user/volunteer/${volunteerId}`
    );
    return response.data;
  },

  updateOwnProfile: async (data: UpdateProfileDto): Promise<UserResponse> => {
    const response = await apiClient.patch<UserResponse>(
      '/user/profile/me',
      data
    );
    return response.data;
  },

  uploadOwnHealthCertificate: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post(
      '/user/profile/me/health-certificate',
      formData
    );
    return response.data;
  },

  deleteOwnHealthCertificate: async (): Promise<void> => {
    await apiClient.delete('/user/profile/me/health-certificate');
  },

  getOwnHealthCertificateUrl: (): string => {
    return `${apiClient.defaults.baseURL}/user/profile/me/health-certificate`;
  },
};

// Types for admin user management
export interface PendingUserResponse {
  id: string;
  email: string;
  role: string;
  volunteerId: string;
  volunteerName: string;
  createdAt: string;
  mapsToPlaceholder: boolean;
}

export interface PendingHealthCertificateResponse {
  volunteerId: string;
  volunteerName: string;
  email: string | null;
  originalName: string | null;
  uploadedAt: string | null;
}

/**
 * Admin API functions - require admin permissions.
 * Used for user management in admin pages.
 */
export const adminApi = {
  downloadVolunteersArchive: async (): Promise<{
    blob: Blob;
    filename: string;
  }> => {
    const response = await apiClient.get<Blob>('/user/volunteers/archive', {
      responseType: 'blob',
      timeout: 120_000,
    });
    const disposition = response.headers['content-disposition'] as
      | string
      | undefined;
    const filename =
      disposition?.match(/filename="?([^";]+)"?/i)?.[1] ??
      `ehrenamtliche-${new Date().toISOString().slice(0, 10)}.zip`;
    return { blob: response.data, filename };
  },

  /**
   * Get all users pending approval
   */
  getPendingUsers: async (): Promise<PendingUserResponse[]> => {
    const response =
      await apiClient.get<PendingUserResponse[]>('/user/pending');
    return response.data;
  },

  getPendingHealthCertificates: async (): Promise<
    PendingHealthCertificateResponse[]
  > => {
    const response = await apiClient.get<PendingHealthCertificateResponse[]>(
      '/user/health-certificates/pending'
    );
    return response.data;
  },

  approveHealthCertificate: async (volunteerId: string): Promise<void> => {
    await apiClient.post(`/user/health-certificates/${volunteerId}/approve`);
  },

  rejectHealthCertificate: async (volunteerId: string): Promise<void> => {
    await apiClient.post(`/user/health-certificates/${volunteerId}/reject`);
  },

  /**
   * Approve a pending user
   */
  approveUser: async (userId: string): Promise<UserResponse> => {
    const response = await apiClient.post<UserResponse>(
      `/user/${userId}/approve`
    );
    return response.data;
  },

  /**
   * Reject a pending user
   */
  rejectUser: async (userId: string): Promise<void> => {
    await apiClient.delete(`/user/${userId}/reject`);
  },

  /**
   * Permanently delete an approved volunteer and all associated data.
   */
  deleteApprovedVolunteer: async (userId: string): Promise<void> => {
    await apiClient.delete(`/user/${userId}`);
  },

  uploadVolunteerContract: async (
    volunteerId: string,
    file: File
  ): Promise<void> => {
    const formData = new FormData();
    formData.append('file', file);
    await apiClient.post(`/user/volunteers/${volunteerId}/contract`, formData);
  },

  uploadVolunteerHealthCertificate: async (
    volunteerId: string,
    file: File
  ): Promise<void> => {
    const formData = new FormData();
    formData.append('file', file);
    await apiClient.post(
      `/user/volunteers/${volunteerId}/health-certificate`,
      formData
    );
  },

  /**
   * Update user role
   */
  updateUserRole: async (
    userId: string,
    role: 'SUPER_ADMIN' | 'ADMIN' | 'VOLUNTEER'
  ): Promise<UserResponse> => {
    const response = await apiClient.patch<UserResponse>(
      `/user/${userId}/role`,
      { role }
    );
    return response.data;
  },
};
